/**
 * Alert Helper Utilities
 * Provides formatting, filtering, and display helper functions for alerts
 */

/**
 * Priority color mapping for Tailwind CSS
 */
export const PRIORITY_COLORS = {
  1: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', dot: 'bg-gray-400' },
  2: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-300', dot: 'bg-blue-500' },
  3: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-300', dot: 'bg-orange-500' },
  4: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-300', dot: 'bg-red-500' },
  low: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', dot: 'bg-gray-400' },
  medium: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-300', dot: 'bg-blue-500' },
  high: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-300', dot: 'bg-orange-500' },
  critical: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-300', dot: 'bg-red-500' }
};

/**
 * Get priority color classes
 * @param {number|string} priority - Priority value (1-4) or label (low, medium, high, critical)
 * @returns {Object} Color classes for bg, text, border, dot
 */
export function getPriorityColor(priority) {
  return PRIORITY_COLORS[priority] || PRIORITY_COLORS[1];
}

/**
 * Get priority label from numeric value
 * @param {number} priority - Priority value (1-4)
 * @returns {string} Priority label
 */
export function getPriorityLabel(priority) {
  const labels = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' };
  return labels[priority] || 'Medium';
}

/**
 * Format timestamp to relative time (e.g., "5 minutes ago")
 * @param {string|Date} timestamp - Timestamp to format
 * @returns {string} Formatted relative time
 */
export function formatTimeAgo(timestamp) {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
  }

  return date.toLocaleDateString();
}

/**
 * Format timestamp for display (short format)
 * @param {string|Date} timestamp - Timestamp to format
 * @returns {string} Formatted date/time
 */
export function formatTimestamp(timestamp) {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * Group alerts by date
 * @param {Array} alerts - Array of alerts
 * @returns {Object} Alerts grouped by date category
 */
export function groupAlertsByDate(alerts) {
  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: []
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  alerts.forEach(alert => {
    const alertDate = new Date(alert.created_at);
    const alertDay = new Date(alertDate.getFullYear(), alertDate.getMonth(), alertDate.getDate());

    if (alertDay >= today) {
      groups.today.push(alert);
    } else if (alertDay >= yesterday) {
      groups.yesterday.push(alert);
    } else if (alertDay >= weekAgo) {
      groups.thisWeek.push(alert);
    } else {
      groups.older.push(alert);
    }
  });

  return groups;
}

/**
 * Filter alerts based on criteria
 * @param {Array} alerts - Array of alerts
 * @param {Object} filters - Filter criteria
 * @param {boolean} filters.unreadOnly - Only show unread alerts
 * @param {string} filters.alertType - Filter by alert type
 * @param {number} filters.minPriority - Minimum priority level
 * @param {string} filters.searchQuery - Search in title/description
 * @param {string} filters.buyerId - Filter by buyer ID
 * @returns {Array} Filtered alerts
 */
export function filterAlerts(alerts, filters = {}) {
  return alerts.filter(alert => {
    // Unread filter
    if (filters.unreadOnly && alert.is_read) {
      return false;
    }

    // Alert type filter
    if (filters.alertType && alert.alert_type !== filters.alertType) {
      return false;
    }

    // Priority filter
    if (filters.minPriority && alert.priority < filters.minPriority) {
      return false;
    }

    // Buyer filter
    if (filters.buyerId && alert.buyer_id !== filters.buyerId) {
      return false;
    }

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const title = (alert.title || '').toLowerCase();
      const description = (alert.description || '').toLowerCase();
      if (!title.includes(query) && !description.includes(query)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Sort alerts by specified criteria
 * @param {Array} alerts - Array of alerts
 * @param {string} sortBy - Sort field ('priority', 'date', 'type')
 * @param {string} sortOrder - Sort order ('asc', 'desc')
 * @returns {Array} Sorted alerts
 */
export function sortAlerts(alerts, sortBy = 'date', sortOrder = 'desc') {
  const sorted = [...alerts];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'priority':
        comparison = a.priority - b.priority;
        break;
      case 'type':
        comparison = (a.alert_type || '').localeCompare(b.alert_type || '');
        break;
      case 'date':
      default:
        comparison = new Date(a.created_at) - new Date(b.created_at);
        break;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });

  // Always put unread alerts first within each sort
  sorted.sort((a, b) => {
    if (!a.is_read && b.is_read) return -1;
    if (a.is_read && !b.is_read) return 1;
    return 0;
  });

  return sorted;
}

/**
 * Get alert icon based on type
 * @param {string} alertType - Alert type
 * @returns {string} Lucide icon name
 */
export function getAlertIcon(alertType) {
  const icons = {
    profile_view: 'Eye',
    interest_spike: 'TrendingUp',
    new_signal: 'Radio',
    match_score: 'Target',
    meeting_request: 'Calendar',
    bd_score_increase: 'ArrowUp',
    bd_score_decrease: 'ArrowDown',
    executive_change: 'User',
    patent_cliff: 'AlertTriangle',
    ma_news: 'Briefcase',
    regulatory_update: 'FileText',
    clinical_trial: 'Microscope',
    partnership: 'Link2'
  };

  return icons[alertType] || 'Bell';
}

/**
 * Get alert color based on type
 * @param {string} alertType - Alert type
 * @returns {string} Tailwind color class
 */
export function getAlertTypeColor(alertType) {
  const colors = {
    profile_view: 'blue',
    interest_spike: 'green',
    new_signal: 'purple',
    match_score: 'orange',
    meeting_request: 'pink',
    bd_score_increase: 'green',
    bd_score_decrease: 'red',
    executive_change: 'yellow',
    patent_cliff: 'red',
    ma_news: 'indigo',
    regulatory_update: 'cyan',
    clinical_trial: 'teal',
    partnership: 'emerald'
  };

  return colors[alertType] || 'gray';
}

/**
 * Check if an alert should trigger a notification sound
 * @param {Object} alert - Alert object
 * @returns {boolean} Whether to play sound
 */
export function shouldPlaySound(alert) {
  // Play sound for high priority alerts
  return alert.priority >= 3;
}

/**
 * Get notification sound based on priority
 * @param {number} priority - Alert priority
 * @returns {string} Sound file path or null
 */
export function getNotificationSound(priority) {
  if (priority >= 4) return '/sounds/critical.mp3';
  if (priority >= 3) return '/sounds/high.mp3';
  return null;
}

/**
 * Format alert for display in notification
 * @param {Object} alert - Alert object
 * @returns {Object} Formatted notification data
 */
export function formatForNotification(alert) {
  return {
    title: alert.title || 'New Alert',
    body: alert.description || '',
    icon: getAlertIcon(alert.alert_type),
    tag: alert.id,
    data: {
      alertId: alert.id,
      url: alert.action_url || '/alerts'
    }
  };
}

/**
 * Calculate engagement rate
 * @param {number} read - Number of read alerts
 * @param {number} total - Total alerts
 * @returns {number} Engagement rate as percentage
 */
export function calculateEngagementRate(read, total) {
  if (!total || total === 0) return 0;
  return Math.round((read / total) * 100);
}

/**
 * Calculate click-through rate
 * @param {number} clicked - Number of clicked alerts
 * @param {number} read - Number of read alerts
 * @returns {number} CTR as percentage
 */
export function calculateCTR(clicked, read) {
  if (!read || read === 0) return 0;
  return Math.round((clicked / read) * 100);
}

export default {
  getPriorityColor,
  getPriorityLabel,
  formatTimeAgo,
  formatTimestamp,
  groupAlertsByDate,
  filterAlerts,
  sortAlerts,
  getAlertIcon,
  getAlertTypeColor,
  shouldPlaySound,
  getNotificationSound,
  formatForNotification,
  calculateEngagementRate,
  calculateCTR,
  PRIORITY_COLORS
};
